import React, { FC, useState } from "react";
import { LuCheckCircle2 } from "react-icons/lu";

import {
  SupportContactResult,
  useContactSupportMutation,
} from "../../../slices/SupportSlice";
import {
  CONTACT_MAX,
  contactProblem,
  errorText,
} from "../../support/support.utils";
import { Spinner } from "../WriteWithAi";
import { charactersLeftText, redactPageUrl } from "./helpChat.utils";
import { primaryButton, secondaryButton } from "./HelpReportForm";

type Props = {
  orgId: string;
  /** "You: … / Assistant: …" when it starts from the chat. */
  chatExcerpt?: string;
  onCancel: () => void;
  onDone: () => void;
};

const field =
  "w-full rounded-[10px] border-[1.5px] border-[#DCE2EA] bg-white px-3 py-2 text-[13.5px] leading-[1.45] text-[#1F2433] outline-none placeholder:text-[#A3A9B8] focus:border-[#6B4EFF]";

/**
 * "Talk to a person" in the help panel (owner 2026-10-25): the question (+ the chat so far) goes to the
 * ProCharted team's Support page; the answer comes by email from support@procharted.com.
 */
export const HelpContactForm: FC<Props> = ({
  orgId,
  chatExcerpt,
  onCancel,
  onDone,
}) => {
  const [send, { isLoading }] = useContactSupportMutation();
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SupportContactResult | null>(null);
  const left = charactersLeftText(message, CONTACT_MAX);

  const submit = async () => {
    const problem = contactProblem(message);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    try {
      setResult(
        await send({
          orgId,
          message: message.trim(),
          chatExcerpt: chatExcerpt || undefined,
          page: redactPageUrl(window.location.href),
        }).unwrap(),
      );
    } catch (caught) {
      setError(
        errorText(caught, "Your message couldn't be sent. Please try again."),
      );
    }
  };

  if (result) {
    return (
      <div
        className={
          "flex flex-1 flex-col items-center justify-center px-8 text-center"
        }>
        <div
          className={
            "flex h-14 w-14 items-center justify-center rounded-full bg-[#E7F6EE] text-[#22A06B]"
          }>
          <LuCheckCircle2 size={30} strokeWidth={2} />
        </div>
        <div className={"mt-4 text-[16px] font-extrabold text-[#1F2433]"}>
          Sent to the ProCharted team
        </div>
        <div className={"mt-1.5 text-[13.5px] leading-[1.5] text-[#3A4256]"}>
          We&apos;ll reply by email to <b>{result.replyTo}</b>, usually within
          one business day.
        </div>
        <div className={"mt-2 text-[12px] text-[#8A90A2]"}>
          Reference: #{result.code}
        </div>
        <button
          type={"button"}
          className={`${secondaryButton} mt-6`}
          onClick={onDone}>
          Back to help
        </button>
      </div>
    );
  }

  return (
    <form
      className={"flex min-h-0 flex-1 flex-col"}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}>
      <div className={"min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4"}>
        <div>
          <div className={"text-[15px] font-extrabold text-[#1F2433]"}>
            Talk to a person
          </div>
          <div className={"mt-1 text-[12.5px] leading-[1.45] text-[#6B7185]"}>
            Ask the ProCharted team. We&apos;ll reply by email to your sign-in
            address, usually within one business day.
          </div>
        </div>
        <div>
          <label
            className={"mb-1.5 block text-[12.5px] font-bold text-[#1F2433]"}
            htmlFor={"help-contact-message"}>
            Your question <span className={"text-[#E5484D]"}>*</span>
          </label>
          <textarea
            id={"help-contact-message"}
            rows={6}
            autoFocus
            className={`${field} resize-none`}
            placeholder={
              "For example: my CSV import from QuickBooks stops at row 3."
            }
            value={message}
            maxLength={CONTACT_MAX}
            onChange={(event) => {
              setMessage(event.target.value.slice(0, CONTACT_MAX));
              if (error) setError(null);
            }}
          />
          {left && (
            <div
              className={"mt-1 text-right text-[11.5px] text-[#8A90A2]"}
              aria-live={"polite"}>
              {left}
            </div>
          )}
        </div>
        {chatExcerpt && (
          <div className={"text-[11.5px] leading-[1.45] text-[#8A90A2]"}>
            The last messages of this chat are attached so you don&apos;t have
            to repeat yourself.
          </div>
        )}
        {error && (
          <div
            role={"alert"}
            className={
              "rounded-[10px] border border-[#F6C9CB] bg-[#FDF0F0] px-3 py-2 text-[12.5px] font-semibold text-[#C8333A]"
            }>
            {error}
          </div>
        )}
      </div>
      <div
        className={
          "flex justify-end gap-2 border-t border-[#EDEFF3] px-5 py-3"
        }>
        <button type={"button"} className={secondaryButton} onClick={onCancel}>
          Cancel
        </button>
        <button type={"submit"} className={primaryButton} disabled={isLoading}>
          {isLoading && (
            <Spinner className={"!border-white/40 !border-t-white"} />
          )}
          {isLoading ? "Sending…" : "Send to the ProCharted team"}
        </button>
      </div>
    </form>
  );
};
